import React, { useEffect, useMemo, useState } from "react";
import { getJobs } from "../../services/apiService";
import { formatCurrency } from "../../utils/formatters";

/**
 * The saved-jobs collection, rendered as the Workbench's left rail.
 *
 * Money here is whatever the API already stored on the job record — this
 * component never computes, sums, or adjusts a number, and renders nothing at
 * all rather than a placeholder zero when the field is absent.
 */

function hasStoredPrice(job) {
  const price = job?.final_price;
  return (
    price !== null &&
    price !== undefined &&
    price !== "" &&
    !Number.isNaN(Number(price))
  );
}

export function formatJobStatus(status) {
  if (!status || typeof status !== "string") return "Draft";
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatJobDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString();
}

function JobsRail({ selectedJobId = null, onSelectJob, onJobsLoaded }) {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        const response = await getJobs();
        if (!isMounted) return;
        const list = Array.isArray(response) ? response : [];
        setJobs(list);
        setError(null);
        if (typeof onJobsLoaded === "function") onJobsLoaded(list);
      } catch (err) {
        if (!isMounted) return;
        console.error("Failed to load jobs:", err);
        setError(err?.message || err?.error || "Failed to load saved jobs.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchJobs();

    return () => {
      isMounted = false;
    };
    // onJobsLoaded is intentionally excluded: the rail fetches once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleJobs = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return jobs;
    return jobs.filter((job) =>
      [job.job_name, job.customer_name, job.status]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle)),
    );
  }, [jobs, filter]);

  return (
    <div className="jobs-rail">
      <div className="jobs-rail__filter">
        <input
          type="search"
          className="wb-input"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter jobs…"
          aria-label="Filter saved jobs"
        />
      </div>

      {isLoading ? <p className="wb-empty">Loading saved jobs…</p> : null}

      {!isLoading && error ? (
        <p className="wb-empty wb-empty--error" role="alert">
          {error}
        </p>
      ) : null}

      {!isLoading && !error && jobs.length === 0 ? (
        <p className="wb-empty">No saved jobs yet</p>
      ) : null}

      {!isLoading && !error && jobs.length > 0 && visibleJobs.length === 0 ? (
        <p className="wb-empty">No jobs match “{filter.trim()}”</p>
      ) : null}

      {!isLoading && !error
        ? visibleJobs.map((job) => (
            <button
              key={job.id}
              type="button"
              className={`wb-item${
                selectedJobId === job.id ? " wb-item--on" : ""
              }`}
              onClick={() => onSelectJob && onSelectJob(job)}
              aria-current={selectedJobId === job.id ? "true" : undefined}
            >
              <span className="wb-item__t">
                <span>{job.job_name || "Untitled job"}</span>
              </span>
              <span className="wb-item__d">
                {job.customer_name || "No customer on file"}
              </span>
              {hasStoredPrice(job) ? (
                <span className="wb-item__m">
                  {formatCurrency(job.final_price)}
                </span>
              ) : null}
            </button>
          ))
        : null}
    </div>
  );
}

export default JobsRail;
